import { GroupDetailsComponent, UpdateBusinessUnitData } from './GroupDetails.types';
import {
  EntityNotFound,
  NewPageWrapper,
  ServerError,
  useTitle,
  getApiConfiguration,
} from '../../common';
import {
  GroupDto,
  GroupsApi,
  GroupsV2Api,
  ResourceDto,
  RoleBindingDto,
  UserDto,
} from '../../api-client';
import { useGroupDetailsQueries } from './hooks/useGroupDetailsQueries';
import { Spinner } from '../../common/components/Spinner/Spinner';
import { GroupUsers } from './ui';
import { useAddUserToGroupModalFacade } from './hooks/useAddUserToGroupModalFacade';
import { AddUserToGroup } from './ui/modals/AddUserToGroupModal/AddUserToGroup';
import { GroupInfo } from './ui/GroupInfo/GroupInfo';
import { useUpdateGroup } from './hooks/useUpdateGroup';
import { GroupRoles } from './ui/GroupRoles/GroupRoles';
import { BottomWrapper } from './GroupDetails.styles';
import { EditGroupDetailsModal } from './ui/modals/EditGroupDetailsModal/EditGroupDetailsModal';
import { useEditGroupDetailsModalFacade } from './hooks/useEditGroupDetailsModalFacade';
import { DeleteGroupModal } from './ui/modals/DeleteGroupModal/DeleteGroupModal';
import { useDeleteGroupModalFacade } from './hooks/useDeleteGroupModalFacade';
import { AssignRoleToGroupModal } from './ui/modals/AssignRoleToGroupModal/AssignRoleToGroupModal';
import { useAssignRoleToGroupModalFacade } from './hooks/useAssignRoleToGroupModalFacade';
import { AddBusinessUnitModal } from './ui/modals/AddBusinessUnitModal/AddBusinessUnitModal';
import { useAddBusinessUnitModalFacade } from './hooks/useAddBusinessUnitModalFacade';
import { useHistory, useParams } from 'react-router-dom';
import { useUnassignRoleModalFacade } from './hooks/useUnassignRoleModalFacade';
import { DeleteConfirmationModal } from '../../common/components/DeleteConfirmationModal/DeleteConfirmationModal';
import { useRemoveUserFromGroupModalFacade } from './hooks/useRemoveUserFromGroupModalFacade';
import { useDeleteBUModalFacade } from './hooks/useDeleteBUModalFacade';

export const GroupDetails: GroupDetailsComponent = () => {
  const groupsApi = new GroupsApi(getApiConfiguration());
  const groupsV2Api = new GroupsV2Api(getApiConfiguration());
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const onRoleClick = (role: RoleBindingDto) =>
    history.push(role.isCustom ? `/custom-roles/${role.roleId}` : `/roles/${role.roleId}`);

  const onUserClick = (user: UserDto) => history.push(`/users/${user.id}`);

  const {
    group,
    groupRoles,
    groupUsers,
    roles,
    businessUnits,
    isFetching,
    fetchError,
    refetchGroupUsers,
    refetchGroupRoles,
  } = useGroupDetailsQueries(groupsApi, groupsV2Api);

  useTitle(`Groups - ${group?.name}`);

  const {
    openAddUserToGroupModal,
    closeAddUserToGroupModal,
    isAddUserToGroupModalOpen,
    onUserAdditionSuccess,
  } = useAddUserToGroupModalFacade(id);

  const { isEditGroupDetailsModalOpen, closeEditGroupDetailsModal, onGroupEditionSuccess } =
    useEditGroupDetailsModalFacade(id);

  const {
    groupUserToDelete,
    closeGroupUserDeleteModal,
    setGroupUserToDelete,
    isRemoving,
    handleRemoveUserFromGroup,
  } = useRemoveUserFromGroupModalFacade(id);

  const { deleteGroup, isDeleting, isDeleteGroupModalOpen, closeDeleteGroupModal } =
    useDeleteGroupModalFacade();

  const {
    isAssigningRole,
    assignRole,
    openAssignRoleToGroupModal,
    closeAssignRoleToGroupModal,
    isAssignRoleToGroupModalOpen,
  } = useAssignRoleToGroupModalFacade(id);

  const {
    isAddBusinessUnitModalOpen,
    openAddBusinessUnitModal,
    closeAddBusinessUnitModal,
    addBusinessUnit,
    isAddingBusinessUnit,
    selectedRole,
  } = useAddBusinessUnitModalFacade(id);

  const {
    mutate: updateGroup,
    error: updateError,
    isLoading: isUpdating,
  } = useUpdateGroup(group?.id as string);

  const {
    buToDelete,
    setBuToDelete,
    closeDeleteBUModal,
    removeBusinessUnit,
    isRemovingBusinessUnit,
  } = useDeleteBUModalFacade();

  const {
    groupRoleToDelete,
    setGroupRoleToDelete,
    closeGroupRoleDeleteModal,
    unassignRole,
    isUnassigning,
  } = useUnassignRoleModalFacade();

  return (
    <NewPageWrapper>
      {!isFetching && !fetchError && <GroupInfo group={group} />}
      <AddUserToGroup
        closeAddUserToGroupModal={closeAddUserToGroupModal}
        isAddUserToGroupModalOpen={isAddUserToGroupModalOpen}
        onUserAdditionSuccess={onUserAdditionSuccess}
      />
      <EditGroupDetailsModal
        group={group as GroupDto}
        updateGroup={updateGroup}
        updateError={updateError}
        isUpdating={isUpdating}
        isEditGroupDetailsModalOpen={isEditGroupDetailsModalOpen}
        closeEditGroupDetailsModal={closeEditGroupDetailsModal}
        onGroupEditionSuccess={onGroupEditionSuccess}
      />
      <DeleteGroupModal
        groupName={group?.name as string}
        open={isDeleteGroupModalOpen}
        onClose={closeDeleteGroupModal}
        isDeleting={isDeleting}
        deleteGroup={deleteGroup}
      />
      <AssignRoleToGroupModal
        groupRoles={groupRoles}
        roles={roles}
        open={isAssignRoleToGroupModalOpen}
        onClose={closeAssignRoleToGroupModal}
        isAssigning={isAssigningRole}
        assignRole={assignRole}
      />
      <AddBusinessUnitModal
        open={isAddBusinessUnitModalOpen}
        onClose={closeAddBusinessUnitModal}
        role={selectedRole as RoleBindingDto}
        businessUnits={businessUnits as ResourceDto[]}
        addBusinessUnit={addBusinessUnit}
        isAdding={isAddingBusinessUnit}
      />
      <DeleteConfirmationModal
        title={
          (groupRoleToDelete && groupRoleToDelete.roleId) || 'Delete group role confirmation modal'
        }
        entity="group role"
        isOpen={!!groupRoleToDelete}
        onClose={closeGroupRoleDeleteModal}
        isDeleting={isUnassigning}
        deleteEntity={() =>
          unassignRole({
            groupId: id,
            roleId: (groupRoleToDelete && groupRoleToDelete.roleId) as string,
            isCustom: groupRoleToDelete && groupRoleToDelete.isCustom,
          })
        }
        dataTestId="unassign-role-confirmation-modal"
      />
      <DeleteConfirmationModal
        title={(groupUserToDelete && groupUserToDelete.name) as string}
        entity="group user"
        isOpen={!!groupUserToDelete}
        onClose={closeGroupUserDeleteModal}
        isDeleting={isRemoving}
        deleteEntity={() => handleRemoveUserFromGroup(groupUserToDelete)}
        dataTestId="remove-user-confirmation-modal"
      />
      <DeleteConfirmationModal
        title={(buToDelete && buToDelete.roleId) as string}
        entity="business unit"
        isOpen={!!buToDelete}
        onClose={closeDeleteBUModal}
        isDeleting={isRemovingBusinessUnit}
        deleteEntity={() => removeBusinessUnit(buToDelete as UpdateBusinessUnitData)}
        dataTestId="delete-user-confirmation-modal"
      />
      {!isFetching && !fetchError && (
        <BottomWrapper container item width="calc(100vw-248px)" flex="1 1 auto" gutter={4}>
          <GroupUsers
            users={groupUsers}
            isLoading={isFetching}
            fetchError={fetchError}
            refetchGroupUsers={refetchGroupUsers}
            onAddButtonClick={openAddUserToGroupModal}
            setGroupUserToDelete={setGroupUserToDelete}
            onUserClick={onUserClick}
          />

          <GroupRoles
            roles={groupRoles}
            onRoleClick={onRoleClick}
            isLoading={isFetching}
            fetchError={fetchError}
            refetchGroupRoles={refetchGroupRoles}
            openAddBusinessUnitModal={openAddBusinessUnitModal}
            setBuToDelete={setBuToDelete}
            setGroupRoleToDelete={setGroupRoleToDelete}
            onAddButtonClick={openAssignRoleToGroupModal}
          />
        </BottomWrapper>
      )}
      {isFetching && <Spinner dataTestId="group-details-spinner" />}
      {!isFetching &&
        (fetchError?.response?.status === 404 ? (
          <EntityNotFound itemTitle="Group" error={fetchError?.message} backRoute="/groups" />
        ) : (
          <ServerError error={fetchError?.message} backRoute="/groups" />
        ))}
    </NewPageWrapper>
  );
};
